/* eslint-disable react/prop-types */
// =============================================================================
// Portfolio Intelligence  —  admin command center sitting ABOVE Risk Review.
// Recommendation-only: shows health, liquidity, exposure, stress tests, and
// per-trip booking priorities.  Never triggers a booking action.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  BarChart2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  CreditCard,
  DollarSign,
  Edit3,
  Gauge,
  Layers,
  Plane,
  RefreshCw,
  Save,
  ShieldAlert,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Vault,
  X as XIcon,
  Zap,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { User } from '@/api/entities';
import { toast } from 'sonner';
import DecisionHistoryDialog from '@/components/portfolio/DecisionHistoryDialog';

// ─── Design tokens (mirror Risk Review) ───────────────────────────────────────

const PANEL =
  'rounded-2xl border border-white/[0.08] bg-zinc-950/60 backdrop-blur-2xl shadow-[0_24px_80px_-40px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.055)]';
const INSET =
  'rounded-xl border border-white/[0.05] bg-gradient-to-br from-white/[0.035] via-transparent to-transparent';
const MICRO = 'text-xs uppercase tracking-[0.2em] text-brand-200/35 font-medium';

const STATUS_PILL = {
  Strong:   'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  Stable:   'text-emerald-100 bg-emerald-950/35 border-emerald-400/20',
  Watch:    'text-brand-100 bg-brand-950/40 border-brand-400/20',
  Fragile:  'text-orange-200 bg-orange-950/45 border-orange-400/25',
  Critical: 'text-rose-200 bg-rose-950/50 border-rose-400/30',
  Unknown:  'text-stone-400 bg-zinc-900/50 border-white/10',
};

const LCR_PILL = {
  Healthy:  'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  Watch:    'text-brand-100 bg-brand-950/40 border-brand-400/20',
  Warning:  'text-orange-200 bg-orange-950/45 border-orange-400/25',
  Critical: 'text-rose-200 bg-rose-950/50 border-rose-400/30',
  Unknown:  'text-stone-400 bg-zinc-900/50 border-white/10',
};

const ALERT_PILL = {
  critical: 'text-rose-200 bg-rose-950/50 border-rose-400/30',
  warning:  'text-brand-100 bg-brand-950/40 border-brand-400/20',
  info:     'text-brand-200 bg-brand-950/40 border-brand-400/20',
};

const ACTION_PILL = {
  BOOK_NOW:                 'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  WATCH:                    'text-brand-100 bg-brand-950/40 border-brand-400/20',
  DELAY:                    'text-stone-300 bg-zinc-900/55 border-white/10',
  MANUAL_REVIEW:            'text-brand-200 bg-brand-950/45 border-brand-400/25',
  DO_NOT_BOOK_UNTIL_PAYMENT: 'text-rose-200 bg-rose-950/50 border-rose-400/30',
};

const PORTFOLIO_AI_PILL = {
  APPROVED_TO_BOOK:    'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  HOLD_FOR_LIQUIDITY:  'text-brand-100 bg-brand-950/40 border-brand-400/20',
  PAYMENT_REQUIRED:    'text-rose-200 bg-rose-950/50 border-rose-400/30',
  MANUAL_REVIEW:       'text-brand-200 bg-brand-950/45 border-brand-400/25',
};

const PORTFOLIO_AI_LABEL = {
  APPROVED_TO_BOOK:    'Approved',
  HOLD_FOR_LIQUIDITY:  'Hold · Liquidity',
  PAYMENT_REQUIRED:    'Payment Required',
  MANUAL_REVIEW:       'Manual Review',
};

const FINAL_PILL = {
  BOOK_NOW:           'text-emerald-100 bg-emerald-950/55 border-emerald-400/35',
  PAYMENT_REQUIRED:   'text-rose-100 bg-rose-950/55 border-rose-400/35',
  HOLD_FOR_LIQUIDITY: 'text-brand-100 bg-brand-950/55 border-brand-400/35',
  WATCH_CLOSELY:      'text-brand-100 bg-brand-950/55 border-brand-400/30',
  MANUAL_REVIEW:      'text-brand-100 bg-brand-950/55 border-brand-400/35',
};

const FINAL_LABEL = {
  BOOK_NOW:           'Book Now',
  PAYMENT_REQUIRED:   'Payment Required',
  HOLD_FOR_LIQUIDITY: 'Hold · Liquidity',
  WATCH_CLOSELY:      'Watch Closely',
  MANUAL_REVIEW:      'Manual Review',
};

const RISK_PILL = {
  A: 'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  B: 'text-emerald-100 bg-emerald-950/35 border-emerald-400/20',
  C: 'text-brand-100 bg-brand-950/40 border-brand-400/20',
  D: 'text-orange-200 bg-orange-950/45 border-orange-400/25',
  F: 'text-rose-200 bg-rose-950/50 border-rose-400/30',
  Unknown: 'text-stone-400 bg-zinc-900/50 border-white/10',
};

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmt$ = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

const fmt$d = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v || 0));

const fmtRatio = (v) =>
  v == null
    ? '—'
    : !Number.isFinite(v)
      ? '∞'
      : v.toFixed(2);

const fmtPct = (v, places = 1) =>
  v == null ? '—' : `${(Number(v) * 100).toFixed(places)}%`;

const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));

// ─── Page ─────────────────────────────────────────────────────────────────────

const CACHE_VERSION = 'v3_excludes_confirmed';
const CACHE_KEY = `defera_portfolio_cache_${CACHE_VERSION}`;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export default function PortfolioIntelligence() {
  const [currentUser, setCurrentUser]   = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [isLoading, setIsLoading]       = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving]         = useState(false);
  const [snapshot, setSnapshot]         = useState(null);
  const [meta, setMeta]                 = useState(null);
  const [error, setError]               = useState(null);
  const [financeConfig, setFinanceConfig] = useState(null);
  const [showFinance, setShowFinance]   = useState(false);

  const canAccess =
    !!currentUser &&
    (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  useEffect(() => {
    (async () => {
      try {
        const me = await User.me();
        setCurrentUser(me);
      } catch { setCurrentUser(null); }
      finally { setIsUserLoading(false); }
    })();
  }, []);

  // ── Cache helpers ────────────────────────────────────────────────────
  const readCache = () => {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('defera_portfolio_cache') && k !== CACHE_KEY)
        .forEach((k) => localStorage.removeItem(k));
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > CACHE_TTL_MS) { localStorage.removeItem(CACHE_KEY); return null; }
      return data;
    } catch { return null; }
  };
  const writeCache = (data) => {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); }
    catch { /* storage full */ }
  };

  // ── Loaders ──────────────────────────────────────────────────────────
  const loadFinance = async () => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const resp = await fetch('/api/admin/portfolio-intelligence/finance-config', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!resp.ok) return;
      const json = await resp.json();
      setFinanceConfig(json.config || null);
    } catch { /* ignore */ }
  };

  const load = async (forceRefresh = false) => {
    if (!canAccess) return;
    if (!forceRefresh) {
      const cached = readCache();
      if (cached) { setSnapshot(cached.snapshot); setMeta(cached.meta); return; }
    }
    try {
      if (!snapshot) setIsLoading(true);
      setIsRefreshing(true);
      setError(null);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const resp = await fetch('/api/admin/portfolio-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      const data = await resp.json();
      setSnapshot(data.snapshot);
      const m = {
        generated_at:           data.generated_at,
        ticketed_trip_count:    data.ticketed_trip_count,
        unticketed_trip_count:  data.unticketed_trip_count,
      };
      setMeta(m);
      writeCache({ snapshot: data.snapshot, meta: m });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      toast.error('Failed to load portfolio intelligence');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const saveSnapshot = async () => {
    try {
      setIsSaving(true);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const resp = await fetch('/api/admin/portfolio-intelligence/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      toast.success('Snapshot saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save snapshot');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (canAccess) {
      load(false);
      loadFinance();
    }
  }, [canAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isUserLoading) return null;
  if (!canAccess) {
    return (
      <div className="min-h-screen bg-[#07070a] flex items-center justify-center text-stone-200 font-normal">
        Admin access required.
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Portfolio Intelligence · Defera</title>
        <meta name="description" content="Portfolio-level risk, liquidity, and booking priority orchestration for Defera Capital and Defera Zero." />
      </Helmet>

      <div className="relative min-h-screen bg-[#07070a]">
        {/* Ambient glows (Risk Review parity) */}
        <div
          className="pointer-events-none fixed inset-0 z-0"
          aria-hidden
          style={{
            background: [
              'radial-gradient(ellipse 90% 60% at 50% -30%, rgba(16, 37, 178,0.13) 0%, transparent 55%)',
              'radial-gradient(ellipse 50% 45% at 96% 12%, rgba(16, 37, 178,0.08) 0%, transparent 50%)',
              'radial-gradient(ellipse 45% 38% at 4% 88%, rgba(16,185,129,0.07) 0%, transparent 45%)',
            ].join(','),
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-6 pb-16">
          <Header
            isRefreshing={isRefreshing}
            onRefresh={() => load(true)}
            onSave={saveSnapshot}
            isSaving={isSaving}
            onEditFinance={() => setShowFinance(true)}
            meta={meta}
          />

          {error && (
            <div className="mt-4 rounded-xl border border-rose-400/25 bg-rose-950/35 text-rose-200/90 text-sm font-normal px-4 py-3">
              {error}
            </div>
          )}

          {isLoading && !snapshot ? (
            <SkeletonBody />
          ) : snapshot ? (
            <div className="mt-6 space-y-4">
              {snapshot.assumptions?.length > 0 && (
                <AssumptionsBanner assumptions={snapshot.assumptions} />
              )}

              {/* Top-line: health + liquidity */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <HealthCard snapshot={snapshot} />
                <LiquidityCard snapshot={snapshot} onEditFinance={() => setShowFinance(true)} />
                <ExposureSummaryCard snapshot={snapshot} />
              </div>

              {/* Required cash + sensitivity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <RequiredCashCard snapshot={snapshot} />
                <SensitivityCard snapshot={snapshot} />
              </div>

              {/* Emergency reserve gap */}
              <ReserveGapCard snapshot={snapshot} onEditFinance={() => setShowFinance(true)} />

              {/* Exposure breakdowns */}
              <DepartureWindowPanel snapshot={snapshot} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ConcentrationPanel title="Top airlines" rows={snapshot.exposure_by_airline} icon={Plane} accent="amber" />
                <ConcentrationPanel title="Top routes" rows={snapshot.exposure_by_route} icon={Layers} accent="indigo" />
              </div>
              <RiskGradePanel rows={snapshot.exposure_by_risk_grade} />

              {/* Stress tests */}
              <StressTestPanel scenarios={snapshot.stress_tests} />

              {/* Alerts + recommended actions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <AlertsPanel alerts={snapshot.alerts} />
                <RecommendedActionsPanel actions={snapshot.recommended_actions} />
              </div>

              {/* Booking priorities */}
              <PrioritiesPanel priorities={snapshot.booking_priorities} />
            </div>
          ) : null}
        </div>

        <FinanceConfigModal
          open={showFinance}
          onClose={() => setShowFinance(false)}
          config={financeConfig}
          onSaved={async (next) => {
            setFinanceConfig(next);
            setShowFinance(false);
            await load(true);
          }}
        />
      </div>
    </>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function Header({ isRefreshing, onRefresh, onSave, isSaving, onEditFinance, meta }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 lg:gap-8">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.36em] text-brand-200/45 font-medium mb-1.5">
            Risk command center
          </p>
          <h1 className="text-2xl sm:text-3xl font-extralight text-white tracking-tight leading-[1.1] flex items-center gap-2 flex-wrap">
            <Gauge className="w-5 h-5 text-brand-200/65 shrink-0" strokeWidth={1.25} />
            <span>
              Portfolio{' '}
              <span className="font-normal text-transparent bg-clip-text bg-gradient-to-r from-brand-100 via-stone-200 to-stone-500">
                Intelligence
              </span>
            </span>
          </h1>
          <p className="text-stone-400 text-sm font-normal mt-1.5 leading-relaxed max-w-xl">
            Aggregate exposure, liquidity, and booking priority across the entire active DC + DZ portfolio. Recommendation-only — no autonomous booking actions.
            {meta?.generated_at && (
              <>
                {' '}
                <span className="text-stone-600">·</span>{' '}
                <span className="text-stone-500">
                  generated {new Date(meta.generated_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              </>
            )}
          </p>
        </div>

        <div className="flex flex-row lg:flex-col gap-2 shrink-0 lg:items-end">
          <Button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="rounded-full h-9 px-5 bg-gradient-to-r from-brand-100 via-brand-200/90 to-brand-300/75 text-zinc-950 hover:opacity-90 border-0 shadow-[0_10px_36px_-12px_rgba(16, 37, 178,0.5)] font-medium tracking-wide text-base"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing…' : 'Refresh snapshot'}
          </Button>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={onSave}
              disabled={isSaving}
              className="rounded-full h-9 px-4 text-stone-300 hover:text-white border border-white/10 hover:bg-white/[0.04] text-sm"
            >
              <Save className={`w-4 h-4 mr-1.5 ${isSaving ? 'animate-pulse' : ''}`} />
              {isSaving ? 'Saving…' : 'Save snapshot'}
            </Button>
            <Button
              variant="ghost"
              onClick={onEditFinance}
              className="rounded-full h-9 px-4 text-stone-400 hover:text-stone-200 border border-white/10 hover:bg-white/[0.04] text-sm"
            >
              <Edit3 className="w-4 h-4 mr-1.5" />
              Finance config
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function AssumptionsBanner({ assumptions }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${INSET} px-3.5 py-2.5`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2 text-brand-200/70 text-xs font-normal tracking-wide">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.25} />
          {assumptions.length} assumption{assumptions.length !== 1 ? 's' : ''} declared
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-stone-500" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-500" />}
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5">
          {assumptions.map((a, i) => (
            <li key={i} className="text-[13px] text-stone-400 font-normal leading-relaxed pl-4 relative before:absolute before:left-0 before:top-2 before:w-1 before:h-1 before:rounded-full before:bg-brand-200/50">
              {a}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SkeletonBody() {
  return (
    <div className="mt-6 space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className={`${PANEL} h-32 animate-pulse`} />
      ))}
    </div>
  );
}

// ─── Cards: Health, Liquidity, Exposure ──────────────────────────────────────

function HealthCard({ snapshot }) {
  const score = snapshot.portfolio_health_score ?? null;
  const status = snapshot.portfolio_status || 'Unknown';
  const pill = STATUS_PILL[status] || STATUS_PILL.Unknown;
  return (
    <div className={`${PANEL} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-white/85">
          <ShieldAlert className="w-4 h-4 text-brand-200/70" strokeWidth={1.25} />
          <span className="font-normal text-sm">Portfolio health</span>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${pill}`}>
          {status}
        </span>
      </div>
      <div className="flex items-end gap-3 mb-3">
        <p className="text-5xl font-extralight text-white tabular-nums leading-none tracking-tight">
          {score ?? '—'}
        </p>
        <p className="text-stone-500 text-sm font-normal pb-1">/ 100</p>
      </div>
      <div className="space-y-1.5">
        {(snapshot.portfolio_health_components || []).map((c) => (
          <ComponentBar key={c.name} label={prettifyName(c.name)} value={c.value} weight={c.weight} />
        ))}
      </div>
    </div>
  );
}

function ComponentBar({ label, value, weight }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const color = v >= 80 ? 'bg-emerald-400/80' : v >= 60 ? 'bg-brand-300/80' : v >= 40 ? 'bg-orange-400/80' : 'bg-rose-400/80';
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-stone-400 font-normal mb-0.5">
        <span>{label} <span className="text-stone-600">· {(weight * 100).toFixed(0)}%</span></span>
        <span className="tabular-nums">{Math.round(v)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-900/70 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

function LiquidityCard({ snapshot, onEditFinance }) {
  const lcr = snapshot.liquidity_coverage_ratio_14d;
  const status = snapshot.liquidity_coverage_status || 'Unknown';
  const pill = LCR_PILL[status] || LCR_PILL.Unknown;
  return (
    <div className={`${PANEL} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-white/85">
          <Activity className="w-4 h-4 text-brand-300/70" strokeWidth={1.25} />
          <span className="font-normal text-sm">Liquidity (14d)</span>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${pill}`}>
          LCR · {status}
        </span>
      </div>
      <div className="flex items-end gap-3 mb-3">
        <p className="text-5xl font-extralight text-white tabular-nums leading-none tracking-tight">
          {fmtRatio(lcr)}
        </p>
        <p className="text-stone-500 text-sm font-normal pb-1">coverage</p>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <KV label="Cash on hand" value={fmt$(snapshot.cash_on_hand)} />
        <KV label="Operating reserve" value={fmt$(snapshot.available_operating_reserve)} />
        <KV label="Expected inflows 14d" value={fmt$(snapshot.expected_inflows_14d)} note="risk-adjusted" />
        <KV
          label="Days to compression"
          value={snapshot.days_until_liquidity_compression == null
            ? '> 180'
            : `${snapshot.days_until_liquidity_compression}d`}
          danger={snapshot.days_until_liquidity_compression != null && snapshot.days_until_liquidity_compression < 21}
        />
      </div>

      <button
        type="button"
        onClick={onEditFinance}
        className="w-full mt-1 text-[11px] text-stone-500 hover:text-stone-300 font-normal tracking-wide transition-colors"
      >
        Edit finance config →
      </button>
    </div>
  );
}

function ExposureSummaryCard({ snapshot }) {
  return (
    <div className={`${PANEL} p-4`}>
      <div className="flex items-center gap-2 text-white/85 mb-3">
        <Layers className="w-4 h-4 text-brand-300/70" strokeWidth={1.25} />
        <span className="font-normal text-sm">Capital exposure</span>
      </div>
      <div className="flex items-end gap-3 mb-3">
        <p className="text-3xl font-extralight text-white tabular-nums leading-none tracking-tight">
          {fmt$(snapshot.total_active_trip_value)}
        </p>
        <p className="text-stone-500 text-sm font-normal pb-1">
          {fmtInt(snapshot.trip_count)} active trip{snapshot.trip_count === 1 ? '' : 's'}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <KV label="Total receivables" value={fmt$(snapshot.total_receivables)} />
        <KV label="Risk-adjusted" value={fmt$(snapshot.risk_adjusted_receivables)} note="collectibility-weighted" />
        <KV label="Unbooked liability" value={fmt$(snapshot.unbooked_future_liability)} />
        <KV label="Booked unpaid" value={fmt$(snapshot.booked_but_unpaid_exposure)} />
      </div>
    </div>
  );
}

function KV({ label, value, note, danger = false }) {
  return (
    <div className={`${INSET} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-normal mb-0.5">
        {label}
      </div>
      <div className={`text-sm font-normal tabular-nums ${danger ? 'text-rose-300' : 'text-white/90'}`}>
        {value}
      </div>
      {note && <div className="text-[10px] text-stone-600 font-normal mt-0.5">{note}</div>}
    </div>
  );
}

function RequiredCashCard({ snapshot }) {
  const rows = [
    { label: '7-day window',  required: snapshot.required_booking_cash_7d,  inflows: snapshot.expected_inflows_7d,  lcr: snapshot.liquidity_coverage_ratio_7d  },
    { label: '14-day window', required: snapshot.required_booking_cash_14d, inflows: snapshot.expected_inflows_14d, lcr: snapshot.liquidity_coverage_ratio_14d },
    { label: '30-day window', required: snapshot.required_booking_cash_30d, inflows: snapshot.expected_inflows_30d, lcr: snapshot.liquidity_coverage_ratio_30d },
  ];
  return (
    <div className={`${PANEL} p-4`}>
      <div className="flex items-center gap-2 text-white/85 mb-3">
        <DollarSign className="w-4 h-4 text-emerald-300/70" strokeWidth={1.25} />
        <span className="font-normal text-sm">Required booking cash</span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const status =
            r.lcr == null ? 'Unknown'
            : !Number.isFinite(r.lcr) || r.lcr >= 1.5 ? 'Healthy'
            : r.lcr >= 1.10 ? 'Watch'
            : r.lcr >= 0.85 ? 'Warning'
            : 'Critical';
          const pill = LCR_PILL[status];
          return (
            <div key={r.label} className={`${INSET} px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap`}>
              <div className="min-w-0">
                <div className="text-xs text-stone-400 font-normal mb-0.5">{r.label}</div>
                <div className="text-base font-normal text-white tabular-nums">
                  {fmt$(r.required)} <span className="text-stone-600 text-xs">required</span>
                </div>
                <div className="text-[11px] text-stone-500 font-normal">
                  +{fmt$(r.inflows)} inflows · LCR {fmtRatio(r.lcr)}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${pill}`}>{status}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SensitivityCard({ snapshot }) {
  const fareSpike = snapshot.fare_spike_exposure || {};
  const defaultExp = snapshot.default_exposure || {};
  return (
    <div className={`${PANEL} p-4`}>
      <div className="flex items-center gap-2 text-white/85 mb-3">
        <TrendingUp className="w-4 h-4 text-rose-300/70" strokeWidth={1.25} />
        <span className="font-normal text-sm">Sensitivity</span>
      </div>

      <div className="space-y-3">
        <div>
          <p className={MICRO}>Fare spike on unbooked liability</p>
          <div className="mt-1.5 grid grid-cols-3 gap-2">
            <SensCell label="+10%" value={fareSpike.plus_10_percent} tone="amber" />
            <SensCell label="+20%" value={fareSpike.plus_20_percent} tone="orange" />
            <SensCell label="+35%" value={fareSpike.plus_35_percent} tone="rose" />
          </div>
        </div>
        <div>
          <p className={MICRO}>Additional default loss</p>
          <div className="mt-1.5 grid grid-cols-4 gap-2">
            <SensCell label="+5%"  value={defaultExp.additional_loss_at_5_percent}  tone="amber" />
            <SensCell label="+10%" value={defaultExp.additional_loss_at_10_percent} tone="amber" />
            <SensCell label="+15%" value={defaultExp.additional_loss_at_15_percent} tone="orange" />
            <SensCell label="+25%" value={defaultExp.additional_loss_at_25_percent} tone="rose" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SensCell({ label, value, tone = 'amber' }) {
  const toneClass =
    tone === 'rose'   ? 'text-rose-300/85'
    : tone === 'orange' ? 'text-orange-300/85'
    : 'text-brand-200/85';
  return (
    <div className={`${INSET} px-2.5 py-2`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-normal">{label}</div>
      <div className={`text-sm font-normal tabular-nums mt-0.5 ${toneClass}`}>{fmt$(value)}</div>
    </div>
  );
}

// ─── Emergency Reserve Gap ───────────────────────────────────────────────────

function ReserveGapCard({ snapshot, onEditFinance }) {
  const r = snapshot.reserve || {};
  const target = Number(r.recommended_emergency_reserve || 0);
  const current = Number(r.available_emergency_reserve || 0);
  const gap = Number(r.reserve_gap || 0);
  const minimum = Number(r.minimum_emergency_reserve || 0);
  const belowMin = minimum > 0 && current < minimum;
  const onTarget = gap <= 0 && target > 0;
  const methodLabel = {
    months_of_unbooked_liability:   'months of unbooked liability',
    percent_of_unbooked_liability:  '% of unbooked liability',
    absolute_target:                'absolute dollar target',
    not_set:                        'not configured',
  }[r.reserve_method] || 'not configured';

  const coveragePct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  const barTone = onTarget
    ? 'bg-emerald-400/80'
    : belowMin
      ? 'bg-rose-400/80'
      : 'bg-brand-300/80';

  return (
    <div className={`${PANEL} p-4`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-white/85">
          <Vault className="w-4 h-4 text-brand-200/70" strokeWidth={1.25} />
          <span className="font-normal text-sm">Emergency reserve</span>
          {r.reserve_method && r.reserve_method !== 'not_set' && (
            <span className="text-[10px] uppercase tracking-wider text-stone-500 font-normal">
              via {methodLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {snapshot.portfolio_health_reserve_amplified && (
            <span className="text-[10px] font-medium uppercase tracking-wider text-rose-200/95 bg-rose-950/50 border border-rose-400/30 rounded-full px-2 py-0.5">
              health weight ↑ amplified
            </span>
          )}
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
              onTarget
                ? 'text-emerald-200 bg-emerald-950/45 border-emerald-400/25'
                : belowMin
                  ? 'text-rose-200 bg-rose-950/50 border-rose-400/30'
                  : gap > 0
                    ? 'text-brand-100 bg-brand-950/45 border-brand-400/25'
                    : 'text-stone-400 bg-zinc-900/55 border-white/10'
            }`}
          >
            {onTarget ? 'On target' : belowMin ? 'Below minimum' : gap > 0 ? 'Gap' : 'Not configured'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KV label="Reserve target"  value={fmt$(target)}  note={`min ${fmt$(minimum)}`} />
        <KV label="Current reserve" value={fmt$(current)} danger={belowMin} />
        <KV label="Reserve gap"     value={fmt$(gap)}     danger={gap > 0} />
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-stone-500 font-normal mb-1">
          <span>Coverage</span>
          <span className="tabular-nums">{Math.round(coveragePct)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-zinc-900/70 overflow-hidden">
          <div className={`h-full ${barTone}`} style={{ width: `${coveragePct}%` }} />
        </div>
      </div>

      {(r.derived_target_months_basis > 0 || r.derived_target_percent_basis > 0) && (
        <p className="mt-2 text-[12.5px] text-stone-400 font-normal leading-relaxed">
          Derived targets:{' '}
          {r.derived_target_months_basis > 0 && (
            <span>months → <span className="text-stone-300 tabular-nums">{fmt$(r.derived_target_months_basis)}</span></span>
          )}
          {r.derived_target_months_basis > 0 && r.derived_target_percent_basis > 0 && <span className="text-stone-700"> · </span>}
          {r.derived_target_percent_basis > 0 && (
            <span>percent → <span className="text-stone-300 tabular-nums">{fmt$(r.derived_target_percent_basis)}</span></span>
          )}
          <span className="text-stone-700"> · </span>
          The higher value drives the reserve gap and health-score reserve component.
        </p>
      )}

      <button
        type="button"
        onClick={onEditFinance}
        className="w-full mt-3 text-[11px] text-stone-500 hover:text-stone-300 font-normal tracking-wide transition-colors"
      >
        Edit reserve target →
      </button>
    </div>
  );
}

// ─── Departure window panel ──────────────────────────────────────────────────

function DepartureWindowPanel({ snapshot }) {
  const rows = snapshot.exposure_by_departure_window || [];
  const chartData = rows.map((r) => ({
    label: r.label,
    booked: r.booked_unpaid_exposure,
    unbooked: r.unbooked_liability,
  }));
  return (
    <div className={PANEL}>
      <div className="px-4 py-3 border-b border-white/[0.06]">
        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-brand-200/65" strokeWidth={1.25} />
          Exposure by departure window
        </h3>
      </div>
      <div className="px-4 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="label" stroke="#ffffff50" tickLine={false} axisLine={false}
                style={{ fontSize: '11px', fill: '#ffffff80', fontWeight: 500 }} />
              <YAxis stroke="#ffffff30" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                tickLine={false} axisLine={false}
                style={{ fontSize: '11px', fill: '#ffffff60', fontWeight: 500 }} />
              <RTooltip
                contentStyle={{ backgroundColor: '#000000f5', border: '1px solid #ffffff15', borderRadius: 8, color: '#fff', fontWeight: 500, padding: '8px 12px' }}
                formatter={(v, name) => [fmt$(v), name === 'booked' ? 'Booked unpaid' : 'Unbooked liability']}
                cursor={{ fill: '#ffffff10' }}
              />
              <Bar dataKey="booked" stackId="exp" fill="#10b981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="unbooked" stackId="exp" fill="#0A1233" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {rows.map((r) => (
            <div key={r.bucket} className={`${INSET} px-3 py-2`}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/85 font-normal">{r.label}</span>
                <span className="text-stone-500 font-normal">{r.trip_count} trip{r.trip_count === 1 ? '' : 's'}</span>
              </div>
              <div className="mt-0.5 flex items-center justify-between text-[11px] text-stone-500 font-normal tabular-nums">
                <span>booked unpaid <span className="text-emerald-300/80">{fmt$(r.booked_unpaid_exposure)}</span></span>
                <span>unbooked <span className="text-brand-300/80">{fmt$(r.unbooked_liability)}</span></span>
              </div>
              <div className="mt-0.5 flex items-center justify-between text-[11px] text-stone-500 font-normal tabular-nums">
                <span>req. booking <span className="text-rose-300/80">{fmt$(r.required_booking_cash)}</span></span>
                <span>avg risk {r.avg_customer_risk_score ?? '—'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Concentration (airlines, routes) ────────────────────────────────────────

function ConcentrationPanel({ title, rows, icon: Icon, accent = 'amber' }) {
  const top = (rows || []).slice(0, 8);
  const colors = {
    amber:  '#2A3868',
    indigo: '#7A8ABF',
    sky:    '#4E6199',
  };
  return (
    <div className={PANEL}>
      <div className="px-4 py-3 border-b border-white/[0.06]">
        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-brand-200/65" strokeWidth={1.25} />
          {title}
        </h3>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        {top.length === 0 && <p className="text-stone-400 text-sm font-normal">No exposure data.</p>}
        {top.map((r) => (
          <div key={r.key} className={`${INSET} px-3 py-2`}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-white/85 font-normal truncate">{r.label}</span>
              <span className="text-xs text-stone-300 font-normal tabular-nums shrink-0">
                {fmt$(r.total_value)} · {fmtPct(r.share_of_portfolio)}
              </span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-zinc-900/70 overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${Math.min(100, (r.share_of_portfolio || 0) * 100)}%`,
                  backgroundColor: colors[accent] || colors.amber,
                  opacity: 0.7,
                }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px] text-stone-500 font-normal">
              <span>{r.trip_count} trip{r.trip_count === 1 ? '' : 's'}</span>
              <span>avg risk {r.avg_risk ?? '—'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskGradePanel({ rows }) {
  const data = (rows || []).map((r) => ({
    name: r.grade,
    value: r.total_value,
    count: r.trip_count,
    share: r.share_of_portfolio,
  }));
  const colorByGrade = { A: '#10b981', B: '#34d399', C: '#4E6199', D: '#4E6199', F: '#f43f5e', Unknown: '#71717a' };
  return (
    <div className={PANEL}>
      <div className="px-4 py-3 border-b border-white/[0.06]">
        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
          <CreditCard className="w-3.5 h-3.5 text-brand-200/65" strokeWidth={1.25} />
          Exposure by customer risk grade
        </h3>
      </div>
      <div className="px-4 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="name" stroke="#ffffff50" tickLine={false} axisLine={false}
                style={{ fontSize: '11px', fill: '#ffffff80', fontWeight: 500 }} />
              <YAxis stroke="#ffffff30" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                tickLine={false} axisLine={false}
                style={{ fontSize: '11px', fill: '#ffffff60', fontWeight: 500 }} />
              <RTooltip
                contentStyle={{ backgroundColor: '#000000f5', border: '1px solid #ffffff15', borderRadius: 8, color: '#fff', fontWeight: 500, padding: '8px 12px' }}
                formatter={(v) => [fmt$(v), 'Exposure']}
                cursor={{ fill: '#ffffff10' }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((d) => (
                  <Cell key={d.name} fill={colorByGrade[d.name] || '#71717a'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1.5">
          {data.map((d) => (
            <div key={d.name} className={`${INSET} px-3 py-2 flex items-center justify-between gap-3`}>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${RISK_PILL[d.name] || RISK_PILL.Unknown}`}>
                Grade {d.name}
              </span>
              <div className="text-right">
                <div className="text-sm text-white/90 font-normal tabular-nums">{fmt$(d.value)}</div>
                <div className="text-[11px] text-stone-500 font-normal">{d.count} · {fmtPct(d.share)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Stress tests ────────────────────────────────────────────────────────────

function StressTestPanel({ scenarios }) {
  if (!scenarios?.length) return null;
  return (
    <div className={PANEL}>
      <div className="px-4 py-3 border-b border-white/[0.06]">
        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-brand-200/65" strokeWidth={1.25} />
          Stress tests
        </h3>
      </div>
      <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {scenarios.map((s) => (
          <ScenarioCard key={s.scenario} s={s} />
        ))}
      </div>
    </div>
  );
}

function ScenarioCard({ s }) {
  const status = s.stressed_portfolio_status || 'Unknown';
  const pill = STATUS_PILL[status] || STATUS_PILL.Unknown;
  const cashDanger = (s.projected_cash_position ?? 0) < 0;
  return (
    <div className={`${INSET} p-3.5 space-y-2.5`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-white truncate">{s.scenario}</p>
          <p className="text-[13px] text-stone-400 font-normal mt-0.5 leading-relaxed">{s.description}</p>
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium border ${pill}`}>
          {s.stressed_portfolio_health_score} · {status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <KV label="Projected cash" value={fmt$(s.projected_cash_position)} danger={cashDanger} />
        <KV label="Emergency reserve" value={fmt$(s.required_emergency_reserve)} danger={s.required_emergency_reserve > 0} />
        <KV label="Stressed LCR" value={fmtRatio(s.stressed_lcr_14d)} />
        <KV label="Trips needing action" value={fmtInt(s.trips_needing_immediate_action)} />
      </div>
      {s.recommended_actions?.length > 0 && (
        <ul className="space-y-1 mt-1">
          {s.recommended_actions.map((a, i) => (
            <li key={i} className="text-[13px] text-stone-300 font-normal leading-relaxed pl-3 relative before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:rounded-full before:bg-brand-300/70">
              {a}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Alerts + recommended actions ────────────────────────────────────────────

function AlertsPanel({ alerts }) {
  return (
    <div className={PANEL}>
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-brand-200/65" strokeWidth={1.25} />
          Alerts
        </h3>
        <span className="text-[11px] text-stone-500 font-normal">{(alerts || []).length}</span>
      </div>
      <div className="px-4 py-3 space-y-1.5 max-h-[460px] overflow-y-auto">
        {(alerts || []).length === 0 && (
          <p className="text-stone-400 text-sm font-normal flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400/80" /> No active alerts.
          </p>
        )}
        {(alerts || []).map((a, i) => (
          <div key={i} className={`${INSET} px-3 py-2`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-medium text-white leading-tight">{a.title}</p>
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium border ${ALERT_PILL[a.severity] || ALERT_PILL.info}`}>
                {a.severity}
              </span>
            </div>
            <p className="text-[13px] text-stone-400 font-normal leading-relaxed">{a.detail}</p>
            {a.booking_reference && (
              <p className="text-[11px] text-stone-600 font-normal mt-1">Ref: {a.booking_reference}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RecommendedActionsPanel({ actions }) {
  const priorityTone = { high: 'text-rose-300/85', medium: 'text-brand-200/85', low: 'text-brand-300/80', info: 'text-stone-400' };
  return (
    <div className={PANEL}>
      <div className="px-4 py-3 border-b border-white/[0.06]">
        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-brand-200/65" strokeWidth={1.25} />
          Recommended actions
        </h3>
      </div>
      <div className="px-4 py-3 space-y-1.5 max-h-[460px] overflow-y-auto">
        {(actions || []).map((a, i) => (
          <div key={i} className={`${INSET} px-3 py-2`}>
            <div className="flex items-start justify-between gap-2 mb-0.5">
              <p className="text-sm font-medium text-white leading-tight">{a.title}</p>
              <span className={`shrink-0 text-[10px] font-medium uppercase tracking-wider ${priorityTone[a.priority] || priorityTone.info}`}>
                {a.priority}
              </span>
            </div>
            <p className="text-[13px] text-stone-400 font-normal leading-relaxed">{a.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Priorities (per-trip booking ranking) ───────────────────────────────────

function PrioritiesPanel({ priorities }) {
  const [showAll, setShowAll] = useState(false);
  const rows = priorities || [];
  const visible = showAll ? rows : rows.slice(0, 12);

  return (
    <div className={PANEL}>
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
          <BarChart2 className="w-3.5 h-3.5 text-brand-200/65" strokeWidth={1.25} />
          Booking priorities
        </h3>
        <span className="text-[11px] text-stone-500 font-normal">{rows.length} trips ranked</span>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {visible.map((p) => (
          <PriorityRow key={p.booking_id || `${p.booking_reference}-${p.route}`} p={p} />
        ))}
      </div>
      {rows.length > 12 && (
        <button
          type="button"
          onClick={() => setShowAll((s) => !s)}
          className="w-full px-4 py-2.5 text-[11px] text-stone-500 hover:text-stone-300 font-normal tracking-wide transition-colors border-t border-white/[0.04]"
        >
          {showAll ? 'Show less' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

function PriorityRow({ p }) {
  const [showHistory, setShowHistory] = useState(false);
  const pill = ACTION_PILL[p.recommended_action] || ACTION_PILL.WATCH;
  const portfolioAiPill = PORTFOLIO_AI_PILL[p.portfolio_ai_action] || PORTFOLIO_AI_PILL.MANUAL_REVIEW;
  const finalPill       = FINAL_PILL[p.final_action] || FINAL_PILL.MANUAL_REVIEW;
  const scoreColor =
    p.booking_priority_score >= 75 ? 'text-emerald-300'
    : p.booking_priority_score >= 60 ? 'text-brand-200'
    : p.booking_priority_score >= 45 ? 'text-brand-300'
    : 'text-stone-400';
  const ticketedDot = p.is_ticketed
    ? <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400/80" title="Ticketed" />
    : <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-400/80" title="Unticketed" />;

  // Deep-link to the matching Risk Review row.
  const riskReviewHref = p.booking_id
    ? `/risk-review?focus=${encodeURIComponent(p.booking_id)}`
    : '/risk-review';

  return (
    <div className="px-4 py-2.5 hover:bg-white/[0.02] transition-colors">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Score column */}
        <div className="shrink-0 w-12 text-center">
          <div className={`text-base font-medium tabular-nums ${scoreColor}`}>{p.booking_priority_score}</div>
          <div className="text-[9px] uppercase tracking-wider text-stone-600 font-normal">priority</div>
        </div>
        {/* Route + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {ticketedDot}
            <span className="text-sm font-normal text-white truncate">{p.route || '—'}</span>
            <span className="text-[11px] text-stone-500 font-normal truncate">{p.marketing_airline || ''}</span>
          </div>
          <div className="text-[11px] text-stone-500 font-normal truncate mt-0.5">
            {p.booking_reference || p.booking_id?.slice(0, 8) || '—'}
            {p.days_until_departure != null && <span className="text-stone-600"> · {p.days_until_departure}d to dep</span>}
            {p.latest_safe_booking_date && <span className="text-stone-600"> · safe by {p.latest_safe_booking_date}</span>}
            {p.risk_grade && <span className="text-stone-600"> · risk {p.risk_grade}</span>}
          </div>
          <p className="text-[12.5px] text-stone-400 font-normal leading-relaxed mt-1 italic">{p.explanation}</p>
          {p.portfolio_impact_summary && (
            <p className="text-[11px] text-stone-600 font-normal mt-0.5">{p.portfolio_impact_summary}</p>
          )}
          {p.final_action_reason && p.final_action_reason !== p.explanation && (
            <p className="text-[12.5px] text-stone-400 font-normal mt-0.5">
              <span className="text-stone-600 uppercase tracking-wider text-[9px] mr-1">Final</span>
              {p.final_action_reason}
            </p>
          )}
        </div>
        {/* Right: dual AI pills + final + view-in-risk-review link */}
        <div className="shrink-0 flex flex-col items-end gap-1">
          {/* Trip AI (recommended_action lives here) */}
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${pill} whitespace-nowrap`}
            title={p.explanation || ''}
          >
            <span className="text-stone-500 uppercase tracking-wider text-[9px] mr-1">Trip</span>
            {p.recommended_action.replace(/_/g, ' ')}
          </span>
          {p.portfolio_ai_action && (
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${portfolioAiPill} whitespace-nowrap`}
              title={p.portfolio_ai_reason || ''}
            >
              <span className="text-white/40 uppercase tracking-wider text-[9px] mr-1">Portfolio</span>
              {PORTFOLIO_AI_LABEL[p.portfolio_ai_action] || p.portfolio_ai_action}
            </span>
          )}
          {p.final_action && (
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${finalPill} whitespace-nowrap`}
              title={p.final_action_reason || ''}
            >
              <span className="text-white/40 uppercase tracking-wider text-[9px] mr-1">Final</span>
              {FINAL_LABEL[p.final_action] || p.final_action}
            </span>
          )}
          <span className="text-[11px] text-stone-500 font-normal tabular-nums">
            {fmt$(p.estimated_ticket_cost)} outlay
          </span>
          <Link
            to={riskReviewHref}
            className="inline-flex items-center gap-1 text-[10px] text-brand-300/85 hover:text-brand-100 border border-brand-400/25 hover:bg-brand-950/40 rounded-full px-2 py-0.5 transition-colors mt-0.5"
            title="Open this trip's Risk Review record"
          >
            View Risk Review
            <ArrowUpRight className="w-2.5 h-2.5" strokeWidth={1.5} />
          </Link>
          {p.booking_id && (
            <button
              type="button"
              onClick={() => setShowHistory(true)}
              className="inline-flex items-center gap-1 text-[10px] text-stone-400 hover:text-stone-200 border border-white/10 hover:bg-white/[0.05] rounded-full px-2 py-0.5 transition-colors"
              title="Read-only audit trail of final_action transitions"
            >
              <ClipboardList className="w-2.5 h-2.5" strokeWidth={1.5} />
              History
            </button>
          )}
        </div>
      </div>
      <DecisionHistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        bookingId={p.booking_id}
        bookingReference={p.booking_reference}
        route={p.route}
      />
    </div>
  );
}

// ─── Finance config modal ───────────────────────────────────────────────────

function FinanceConfigModal({ open, onClose, config, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const c = config || {};
      setDraft({
        cash_on_hand:                  centsToInput(c.cash_on_hand_cents),
        available_operating_reserve:   centsToInput(c.available_operating_reserve_cents),
        minimum_required_reserve:      centsToInput(c.minimum_required_reserve_cents),
        payment_processor_hold:        centsToInput(c.payment_processor_hold_cents),
        expected_inflows_next_7_days:  centsToInput(c.expected_inflows_next_7_days_cents),
        expected_inflows_next_14_days: centsToInput(c.expected_inflows_next_14_days_cents),
        expected_inflows_next_30_days: centsToInput(c.expected_inflows_next_30_days_cents),
        // Emergency reserve (migration 053)
        available_emergency_reserve:                       centsToInput(c.available_emergency_reserve_cents),
        target_emergency_reserve:                          centsToInput(c.target_emergency_reserve_cents),
        minimum_emergency_reserve:                         centsToInput(c.minimum_emergency_reserve_cents),
        reserve_coverage_months:                           c.reserve_coverage_months == null ? '' : String(c.reserve_coverage_months),
        reserve_coverage_percent_of_unbooked_liability:    c.reserve_coverage_percent_of_unbooked_liability == null
                                                              ? ''
                                                              : String(c.reserve_coverage_percent_of_unbooked_liability),
        notes: c.notes || '',
      });
    }
  }, [open, config]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const resp = await fetch('/api/admin/portfolio-intelligence/finance-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(draft),
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      const json = await resp.json();
      toast.success('Finance config saved');
      await onSaved?.(json.config);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0d] border border-white/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white font-light text-lg flex items-center gap-2">
            <Edit3 className="w-4 h-4 text-brand-200/70" strokeWidth={1.25} />
            Finance config
          </DialogTitle>
          <DialogDescription className="text-stone-400 font-normal text-sm">
            These inputs drive liquidity calculations. All values in USD. Expected inflow overrides leave blank to auto-derive from customer payment schedules.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <FinanceField label="Cash on hand"                  k="cash_on_hand"                  draft={draft} setDraft={setDraft} />
          <FinanceField label="Available operating reserve"   k="available_operating_reserve"   draft={draft} setDraft={setDraft} />
          <FinanceField label="Minimum required reserve"      k="minimum_required_reserve"      draft={draft} setDraft={setDraft} />
          <FinanceField label="Payment processor hold"        k="payment_processor_hold"        draft={draft} setDraft={setDraft} />
          <FinanceField label="Expected inflows · next 7d (override)"  k="expected_inflows_next_7_days"  draft={draft} setDraft={setDraft} optional />
          <FinanceField label="Expected inflows · next 14d (override)" k="expected_inflows_next_14_days" draft={draft} setDraft={setDraft} optional />
          <FinanceField label="Expected inflows · next 30d (override)" k="expected_inflows_next_30_days" draft={draft} setDraft={setDraft} optional />
        </div>

        {/* ── Emergency reserve target ─────────────────────────────────── */}
        <div className="mt-4 pt-3 border-t border-white/[0.06]">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-200/55 font-medium mb-2">
            Emergency reserve target
          </p>
          <p className="text-[12.5px] text-stone-400 font-normal leading-relaxed mb-2.5">
            Distinct "wartime" fund separate from the operating reserve. The reserve gap surfaces in the dashboard and amplifies the health-score reserve weight when severe/moderate stress tests project negative cash.
            The system uses whichever derived target is higher (absolute, months-of-burn, or percent-of-unbooked).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FinanceField label="Current emergency reserve"     k="available_emergency_reserve" draft={draft} setDraft={setDraft} />
            <FinanceField label="Target emergency reserve"      k="target_emergency_reserve"    draft={draft} setDraft={setDraft} />
            <FinanceField label="Minimum emergency reserve"     k="minimum_emergency_reserve"   draft={draft} setDraft={setDraft} />
            <FinanceField
              label="Coverage · months of unbooked liability"
              k="reserve_coverage_months"
              draft={draft}
              setDraft={setDraft}
              optional
              kind="months"
            />
            <FinanceField
              label="Coverage · % of unbooked liability"
              k="reserve_coverage_percent_of_unbooked_liability"
              draft={draft}
              setDraft={setDraft}
              optional
              kind="percent"
            />
          </div>
        </div>
        <div className="mt-2">
          <label className="text-xs text-stone-500 font-normal uppercase tracking-widest">Notes</label>
          <Input
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            placeholder="Optional context for this config"
            className="bg-white/[0.03] border-white/10 text-white mt-1 font-normal"
          />
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} className="rounded-full text-stone-400 border border-white/10">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-gradient-to-r from-brand-100 via-brand-200/90 to-brand-300/75 text-zinc-950 hover:opacity-90 font-medium"
          >
            <Save className={`w-4 h-4 mr-1.5 ${saving ? 'animate-pulse' : ''}`} />
            {saving ? 'Saving…' : 'Save config'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FinanceField({ label, k, draft, setDraft, optional = false, kind = 'dollars' }) {
  const placeholder = kind === 'months' ? 'e.g. 3' : kind === 'percent' ? 'e.g. 25' : optional ? 'auto-derived' : '0.00';
  const step = kind === 'months' ? '0.25' : kind === 'percent' ? '0.5' : '0.01';
  return (
    <div>
      <label className="text-[11px] text-stone-500 font-normal uppercase tracking-widest">
        {label} {optional && <span className="text-stone-700 normal-case">(optional)</span>}
      </label>
      <Input
        type="number"
        step={step}
        value={draft[k]}
        onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
        placeholder={placeholder}
        className="bg-white/[0.03] border-white/10 text-white mt-1 font-normal tabular-nums"
      />
    </div>
  );
}

function centsToInput(cents) {
  if (cents == null) return '';
  const n = Number(cents) / 100;
  return Number.isFinite(n) ? String(n) : '';
}

// ─── Misc helpers ────────────────────────────────────────────────────────────

function prettifyName(s) {
  return String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
